// Start here's interactive parts, ported from the live course site's components (CardJudge, Quiz,
// Walkthrough, Calculator, Drill, ChannelMatrix and Channels, ChannelChoice, BudgetMapper,
// Assignment and Gate) and drawn in POYT's look. Each one shows the member's saved answers and saves
// what they change as they go, through save(delta, { done }) from Screen.jsx, which merges the change
// on screen at once and writes it (the database keeps a call once made, works out the scores and
// holds the gate). While staff view as a member (readOnly) nothing that would save can be pressed:
// the member's answers show as they are, and only reading (stepping through a worked example,
// opening a channel, trying the calculator) still works, saving nothing.
import { useEffect, useRef, useState } from 'react'
import { readOnlyControl, readOnlyField } from '../../../lib/viewAs.jsx'
import { Chevron } from '../../../product/Icons.jsx'
import { COPY, gbp } from './copy.js'
import { Md } from './Md.jsx'
import { budgetTotals, budgetVerdict, houseRight, parseAmount } from './model.js'

const obj = v => (v && typeof v === 'object' && !Array.isArray(v) ? v : {})
const quiet = p => { if (p && typeof p.catch === 'function') p.catch(() => {}) }

// ---- The comparables filter (binary) and the street sorter (triple): each card's first call stands
export function CardJudge({ cfg, mode, answers, save, readOnly }) {
  const cards = Array.isArray(cfg.cards) ? cfg.cards : []
  const calls = obj(answers.answers)
  const labels = mode === 'binary'
    ? [['keep', COPY.keepIt], ['reject', COPY.leaveItOut]]
    : [['keep', COPY.keep], ['amber', COPY.amber], ['avoid', COPY.avoid]]
  function pick(card, choice) {
    if (calls[card.id] || readOnly) return
    quiet(save({ answers: { ...calls, [card.id]: choice } }))
  }
  const tried = cards.filter(c => calls[c.id]).length
  const right = cards.filter(c => calls[c.id] === c.verdict).length
  return (
    <div className="ps-ex">
      {tried > 0 && <p className="ps-tally" aria-live="polite">{COPY.tally(right, tried, cards.length - tried)}</p>}
      {cards.map(c => {
        const a = calls[c.id]
        return (
          <div className="ps-ex-card" key={c.id} data-card={c.id}>
            <p className="ps-q" id={`ps-card-${c.id}`}>{c.text}</p>
            <div className="ps-opts" role="group" aria-labelledby={`ps-card-${c.id}`}>
              {labels.map(([val, lab]) => {
                const cls = ['ps-opt']
                if (a === val) cls.push(val === c.verdict ? 'ps-opt--right' : 'ps-opt--wrong')
                if (a && a !== c.verdict && val === c.verdict) cls.push('ps-opt--reveal')
                return (
                  <button key={val} type="button" className={cls.join(' ')} disabled={Boolean(a) || readOnly}
                    aria-pressed={a === val} {...(a ? {} : readOnlyControl(readOnly))} onClick={() => pick(c, val)}>{lab}</button>
                )
              })}
            </div>
            {a && (
              <p className={a === c.verdict ? 'ps-fb ps-fb--right' : 'ps-fb ps-fb--wrong'}>
                <span className="ps-fb-tag">{a === c.verdict ? COPY.right : COPY.notThisOne}</span>{c.feedback}
              </p>
            )}
          </div>
        )
      })}
      {tried === cards.length && cards.length > 0 && cfg.done_md && <Md text={cfg.done_md} className="ps-done-md" />}
    </div>
  )
}

// ---- The quick check: the first answer stands
export function Quiz({ quiz, answers, save, readOnly }) {
  const chosen = Number.isInteger(answers.quiz?.chosen) ? answers.quiz.chosen : null
  const options = Array.isArray(quiz.options) ? quiz.options : []
  function pick(i) {
    if (chosen !== null || readOnly) return
    quiet(save({ quiz: { chosen: i } }))
  }
  const right = chosen !== null && chosen === quiz.answer
  return (
    <div className="ps-ex">
      <div className="ps-ex-card ps-quiz">
        <div className="ps-eyebrow ps-eyebrow--small">{COPY.quickCheck}</div>
        <p className="ps-q" id="ps-quiz-q">{quiz.question}</p>
        <div className="ps-opts ps-opts--stack" role="group" aria-labelledby="ps-quiz-q">
          {options.map((o, i) => {
            const cls = ['ps-opt']
            if (chosen === i) cls.push(i === quiz.answer ? 'ps-opt--right' : 'ps-opt--wrong')
            if (chosen !== null && chosen !== quiz.answer && i === quiz.answer) cls.push('ps-opt--reveal')
            return (
              <button key={i} type="button" className={cls.join(' ')} disabled={chosen !== null || readOnly} aria-pressed={chosen === i}
                {...(chosen === null ? readOnlyControl(readOnly) : {})} onClick={() => pick(i)}>{o}</button>
            )
          })}
        </div>
        {chosen !== null && (
          <p className={right ? 'ps-fb ps-fb--right' : 'ps-fb ps-fb--wrong'}>
            <span className="ps-fb-tag">{right ? COPY.right : COPY.notQuite}</span>
            {right ? quiz.feedback_correct : quiz.feedback_wrong}
          </p>
        )}
      </div>
    </div>
  )
}

// ---- A worked example, revealed a step at a time; stepping to the end saves it as done
export function Walkthrough({ cfg, answers, save, readOnly }) {
  const steps = Array.isArray(cfg.steps) ? cfg.steps : []
  const walked = answers.walk_done === true
  const [shown, setShown] = useState(walked ? steps.length : Math.min(1, steps.length))
  const sent = useRef(walked)
  useEffect(() => {
    if (shown >= steps.length && !sent.current && !readOnly) {
      sent.current = true
      quiet(save({ walk_done: true }))
    }
  }, [shown, steps.length, readOnly, save])
  const slots = Array.isArray(cfg.figures) ? cfg.figures : []
  const values = {}
  steps.slice(0, shown).forEach(s => { Object.assign(values, obj(s.set)) })
  return (
    <div className="ps-ex">
      {slots.length > 0 && (
        <dl className="ps-run">
          {slots.map(f => (
            <div key={f.key} className={values[f.key] === undefined ? 'ps-run-f ps-run-f--pending' : 'ps-run-f'}>
              <dt>{f.label}</dt>
              <dd>{values[f.key] === undefined ? <span className="ps-run-none"><span className="sr-only">{COPY.figureNotYet}</span></span> : values[f.key]}</dd>
            </div>
          ))}
        </dl>
      )}
      <ol className="ps-walk">
        {steps.slice(0, shown).map((s, i) => (
          <li className="ps-walk-step" key={i}>
            <h3 className="ps-walk-h">{s.title}</h3>
            <Md text={s.body} />
          </li>
        ))}
      </ol>
      {shown < steps.length && (
        <button type="button" className="ps-btn ps-btn--quiet ps-walk-next" onClick={() => setShown(shown + 1)}>{COPY.nextStepOfDeal}</button>
      )}
    </div>
  )
}

// ---- The cash offer calculator: trying it once saves that it was tried
export function Calculator({ answers, save, readOnly }) {
  const [needsWork, setNeedsWork] = useState(true)
  const [value, setValue] = useState(200000)
  const [refurb, setRefurb] = useState(25000)
  const [costs, setCosts] = useState(8000)
  const touched = useRef(answers.calc_touched === true)
  function touch() {
    if (touched.current || readOnly) return
    touched.current = true
    quiet(save({ calc_touched: true }))
  }
  const v = Number(value) || 0
  const r = needsWork ? (Number(refurb) || 0) : 0
  const c = Number(costs) || 0
  const offer = 0.75 * v - r - c
  return (
    <div className="ps-calc">
      <div className="ps-calc-row">
        <span className="ps-calc-label" id="ps-calc-work">{COPY.needsWork}</span>
        <div className="ps-toggle" role="group" aria-labelledby="ps-calc-work">
          <button type="button" className={needsWork ? 'ps-toggle-on' : ''} aria-pressed={needsWork} onClick={() => { touch(); setNeedsWork(true) }}>{COPY.yesRefurb}</button>
          <button type="button" className={!needsWork ? 'ps-toggle-on' : ''} aria-pressed={!needsWork} onClick={() => { touch(); setNeedsWork(false) }}>{COPY.noWork}</button>
        </div>
      </div>
      <div className="ps-calc-row">
        <label className="ps-calc-label" htmlFor="ps-calc-value">{needsWork ? COPY.gdv : COPY.asIs}<span className="ps-calc-sub">{needsWork ? COPY.gdvSub : COPY.asIsSub}</span></label>
        <input id="ps-calc-value" type="number" inputMode="numeric" value={value} onChange={e => { touch(); setValue(e.target.value) }} />
      </div>
      {needsWork && (
        <div className="ps-calc-row">
          <label className="ps-calc-label" htmlFor="ps-calc-refurb">{COPY.refurb}<span className="ps-calc-sub">{COPY.refurbSub}</span></label>
          <input id="ps-calc-refurb" type="number" inputMode="numeric" value={refurb} onChange={e => { touch(); setRefurb(e.target.value) }} />
        </div>
      )}
      <div className="ps-calc-row">
        <label className="ps-calc-label" htmlFor="ps-calc-costs">{COPY.costs}<span className="ps-calc-sub">{COPY.costsSub}</span></label>
        <input id="ps-calc-costs" type="number" inputMode="numeric" value={costs} onChange={e => { touch(); setCosts(e.target.value) }} />
      </div>
      <div className="ps-calc-out" aria-live="polite">
        <div className="ps-calc-out-label">{COPY.yourCashOffer}</div>
        <div className="ps-calc-out-num">{gbp(offer)}</div>
        <p className="ps-calc-out-working">{COPY.working(gbp(v), gbp(0.75 * v), gbp(r), gbp(c))}</p>
      </div>
    </div>
  )
}

// ---- The 3-house drill: right within £500 of the answer, and a house once right stays right
export function Drill({ cfg, answers, save, readOnly }) {
  const houses = Array.isArray(cfg.houses) ? cfg.houses : []
  const state = obj(answers.houses)
  const [inputs, setInputs] = useState({})
  const rightOf = h => (typeof state[h.id]?.correct === 'boolean' ? state[h.id].correct : houseRight(h, state[h.id]?.entered))
  function check(h) {
    if (readOnly) return
    const val = parseAmount(inputs[h.id])
    if (val === null) return
    const next = {}
    for (const x of houses) if (typeof state[x.id]?.entered === 'number') next[x.id] = { entered: state[x.id].entered }
    next[h.id] = { entered: val }
    quiet(save({ houses: next }))
  }
  const doneAll = houses.length > 0 && houses.every(rightOf)
  return (
    <div className="ps-ex">
      {houses.map((h, i) => {
        const s = state[h.id]
        const ok = s ? rightOf(h) : false
        return (
          <div className="ps-ex-card" key={h.id} data-house={h.id}>
            <div className="ps-eyebrow ps-eyebrow--small">{COPY.house(i + 1, h.name)}</div>
            <p className="ps-q ps-q--plain">{h.brief}</p>
            {!ok && (
              <form className="ps-drill-in" onSubmit={e => { e.preventDefault(); check(h) }}>
                <label className="sr-only" htmlFor={`ps-house-${h.id}`}>{COPY.houseAnswerLabel(i + 1)}</label>
                <input id={`ps-house-${h.id}`} type="text" inputMode="numeric" placeholder="£" autoComplete="off" value={inputs[h.id] || ''}
                  onChange={e => setInputs({ ...inputs, [h.id]: e.target.value })} {...readOnlyField(readOnly)} />
                <button type="submit" className="ps-btn" {...readOnlyControl(readOnly)}>{COPY.check}</button>
              </form>
            )}
            {s && typeof s.entered === 'number' && (
              <p className={ok ? 'ps-fb ps-fb--right' : 'ps-fb ps-fb--wrong'}>
                <span className="ps-fb-tag">{ok ? COPY.right : COPY.notYet}</span>
                {ok ? h.working : COPY.youEntered(gbp(s.entered), h.hint)}
              </p>
            )}
          </div>
        )
      })}
      {doneAll && cfg.done_md && <Md text={cfg.done_md} className="ps-done-md" />}
    </div>
  )
}

// ---- The 7 channels: the table to compare, then each opened in turn (each one opened is saved)
function ChannelMatrix({ channels }) {
  const cols = [COPY.colChannel, COPY.colCost, COPY.colSpeed, COPY.colEffort]
  return (
    <figure className="ps-vis">
      <figcaption className="ps-vis-head">{COPY.sevenTitle(channels.length)}</figcaption>
      <p className="ps-vis-sub">{COPY.sevenSub}</p>
      <table className="ps-tbl ps-tbl--stack">
        <thead><tr>{cols.map(c => <th key={c} scope="col">{c}</th>)}</tr></thead>
        <tbody>
          {channels.map((c, i) => (
            <tr key={i}>
              <td className="ps-tbl-lead" data-label={cols[0]}>{c.name}</td>
              <td data-label={cols[1]}>{c.cost}</td>
              <td data-label={cols[2]}>{c.speed}</td>
              <td data-label={cols[3]}>{c.effort}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </figure>
  )
}

export function Channels({ cfg, answers, save, readOnly }) {
  const channels = Array.isArray(cfg.channels) ? cfg.channels : []
  const opened = obj(answers.chan_opened)
  const [open, setOpen] = useState(0)
  const first = useRef(false)
  useEffect(() => {
    // the first is open as the screen opens, so it counts as opened (as on live)
    if (first.current || readOnly || !channels.length) return
    first.current = true
    if (opened['0'] !== true) quiet(save({ chan_opened: { ...opened, 0: true } }))
  }, [])
  function toggle(i) {
    const next = open === i ? -1 : i
    setOpen(next)
    if (next >= 0 && opened[String(next)] !== true && !readOnly) quiet(save({ chan_opened: { ...opened, [String(next)]: true } }))
  }
  return (
    <div className="ps-ex">
      <ChannelMatrix channels={channels} />
      <div className="ps-chans">
        {channels.map((c, i) => (
          <div className={open === i ? 'ps-chan ps-chan--open' : 'ps-chan'} key={i}>
            <h3 className="ps-chan-h">
              <button type="button" className="ps-chan-head" aria-expanded={open === i} aria-controls={`ps-chan-${i}`} onClick={() => toggle(i)}>
                <span className="ps-chan-n">{i + 1}</span><span className="ps-chan-t">{c.name}</span><Chevron size={18} open={open === i} />
              </button>
            </h3>
            {open === i && (
              <div className="ps-chan-body" id={`ps-chan-${i}`}>
                <p>{c.what}</p>
                <p className="ps-chan-meta">
                  <span><b>{COPY.costLabel}</b> {c.cost}</span>
                  <span><b>{COPY.speedLabel}</b> {c.speed}</span>
                  <span><b>{COPY.effortLabel}</b> {c.effort}</span>
                </p>
                {c.note && <p className="ps-chan-note">{c.note}</p>}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}

// ---- The campaign rule: letters, plus 1 more channel picked here (it can be changed)
export function ChannelChoice({ cfg, answers, save, readOnly }) {
  const options = Array.isArray(cfg.options) ? cfg.options : []
  const choice = typeof answers.secondary === 'string' ? answers.secondary : null
  const picked = options.find(o => o.id === choice) || null
  function pick(id) {
    if (readOnly || id === choice) return
    quiet(save({ secondary: id }))
  }
  return (
    <div className="ps-ex">
      <div className="ps-locked-chan">{COPY.channelOne}<span>{COPY.channelOneNote}</span></div>
      <div className="ps-pick" role="radiogroup" aria-label={COPY.chooseSecond}>
        {options.map(o => (
          <button key={o.id} type="button" role="radio" aria-checked={choice === o.id}
            className={choice === o.id ? 'ps-opt ps-opt--chosen' : 'ps-opt'} {...readOnlyControl(readOnly)} onClick={() => pick(o.id)}>
            {o.label}<span className="ps-opt-hint">{o.hint}</span>
          </button>
        ))}
      </div>
      {picked && <p className="ps-fb ps-fb--right" aria-live="polite"><span className="ps-fb-tag">{COPY.recorded}</span>{COPY.recordedLine(picked.label)}</p>}
      <p className="ps-small">{COPY.agentsNote}</p>
    </div>
  )
}

// ---- The letter budget: the plan, saved with its total. chosen: the campaign rule's recorded choice.
export function BudgetMapper({ cfg, answers, save, readOnly, chosen }) {
  const secOpts = obj(cfg.secondary_costs)
  const chosenId = (typeof answers.secondary === 'string' && answers.secondary) || chosen || 'none'
  const [batch, setBatch] = useState(typeof answers.batch === 'number' && answers.batch ? answers.batch : 100)
  const [sends, setSends] = useState(typeof answers.sends === 'number' && answers.sends ? answers.sends : 2)
  const [cpl, setCpl] = useState(typeof answers.cpl === 'number' ? answers.cpl : 1.0)
  const [secondary, setSecondary] = useState(secOpts[chosenId] ? chosenId : Object.keys(secOpts)[0] || 'none')
  const [secSpend, setSecSpend] = useState(typeof answers.secSpend === 'number' ? answers.secSpend : (secOpts[chosenId]?.default ?? 0))
  const totals = budgetTotals({ batch, sends, cpl, secSpend })
  const verdict = budgetVerdict(totals, secondary)
  const verdicts = { lettersOver: COPY.verdictLettersOver, inside: COPY.verdictInside, ads: COPY.verdictAds, over: COPY.verdictOver }
  function savePlan() {
    if (readOnly) return
    quiet(save({
      batch: Number(batch) || 0, sends: Number(sends) || 0, cpl: Number(cpl) || 0, secondary,
      secSpend: Number(secSpend) || 0, total: Math.round(totals.total),
    }))
  }
  return (
    <div className="ps-calc">
      <div className="ps-calc-row">
        <label className="ps-calc-label" htmlFor="ps-b-batch">{COPY.batch}<span className="ps-calc-sub">{COPY.batchSub}</span></label>
        <input id="ps-b-batch" type="number" inputMode="numeric" value={batch} onChange={e => setBatch(e.target.value)} {...readOnlyField(readOnly)} />
      </div>
      <div className="ps-calc-row">
        <label className="ps-calc-label" htmlFor="ps-b-sends">{COPY.sends}<span className="ps-calc-sub">{COPY.sendsSub}</span></label>
        <input id="ps-b-sends" type="number" inputMode="numeric" value={sends} onChange={e => setSends(e.target.value)} {...readOnlyField(readOnly)} />
      </div>
      <div className="ps-calc-row">
        <label className="ps-calc-label" htmlFor="ps-b-cpl">{COPY.cpl}<span className="ps-calc-sub">{COPY.cplSub}</span></label>
        <input id="ps-b-cpl" type="number" inputMode="decimal" step="0.05" value={cpl} onChange={e => setCpl(e.target.value)} {...readOnlyField(readOnly)} />
      </div>
      <div className="ps-calc-row">
        <label className="ps-calc-label" htmlFor="ps-b-second">{COPY.second}<span className="ps-calc-sub">{chosenId !== 'none' ? COPY.secondFromChoice : COPY.secondNoChoice}</span></label>
        <select id="ps-b-second" value={secondary} disabled={readOnly}
          onChange={e => { const id = e.target.value; setSecondary(id); setSecSpend(secOpts[id] ? secOpts[id].default : 0) }}>
          {Object.entries(secOpts).map(([id, o]) => <option key={id} value={id}>{o.label}</option>)}
        </select>
      </div>
      <div className="ps-calc-row">
        <label className="ps-calc-label" htmlFor="ps-b-spend">{COPY.secondSpend}<span className="ps-calc-sub">{secOpts[secondary]?.note || ''}</span></label>
        <input id="ps-b-spend" type="number" inputMode="numeric" value={secSpend} onChange={e => setSecSpend(e.target.value)} {...readOnlyField(readOnly)} />
      </div>
      <div className="ps-totals">
        <div className="ps-totline"><span>{COPY.lettersLine(batch || 0, sends || 0, (Number(cpl) || 0).toFixed(2))}</span><span>{gbp(totals.letters)}</span></div>
        <div className="ps-totline"><span>{COPY.secondLine}</span><span>{gbp(Number(secSpend) || 0)}</span></div>
        <div className="ps-totline ps-totline--total"><span>{COPY.totalLine}</span><b>{gbp(totals.total)}</b></div>
      </div>
      <div className="ps-band" aria-hidden="true">
        <div className="ps-band-track">
          <div className="ps-band-zone" />
          <div className="ps-band-fill" style={{ width: `${totals.pct}%` }} />
        </div>
        <div className="ps-band-labels">{COPY.band.map(b => <span key={b}>{b}</span>)}</div>
      </div>
      <p className="ps-fb ps-fb--right" aria-live="polite">{verdicts[verdict]}</p>
      <div className="ps-save-row">
        <button type="button" className="ps-btn" onClick={savePlan} {...readOnlyControl(readOnly)}>{COPY.savePlan}</button>
        {typeof answers.total === 'number' && <span className="ps-small">{COPY.savedPlan(gbp(answers.total))}</span>}
      </div>
    </div>
  )
}

// ---- An assignment's ticks, saved as they go
export function Assignment({ cfg, answers, save, readOnly }) {
  const items = Array.isArray(cfg.items) ? cfg.items : []
  const ticks = obj(answers.ticks)
  function toggle(i) {
    if (readOnly) return
    quiet(save({ ticks: { ...ticks, [String(i)]: ticks[String(i)] !== true } }))
  }
  return (
    <fieldset className="ps-ex ps-checks">
      <legend className="ps-eyebrow ps-eyebrow--small">{cfg.label || COPY.yourAssignment}</legend>
      {items.map((t, i) => (
        <label className={ticks[String(i)] === true ? 'ps-check ps-check--on' : 'ps-check'} key={i}>
          <input type="checkbox" checked={ticks[String(i)] === true} onChange={() => toggle(i)} {...readOnlyControl(readOnly)} />
          <span className="ps-check-text">{t}</span>
        </label>
      ))}
      <p className="ps-small">{COPY.ticksSave}</p>
    </fieldset>
  )
}

// ---- The gate: 3 ticks, and the screen (and with it Start here) is done once all 3 are true.
// secondLabel: the campaign rule's recorded second channel, under the 3rd tick.
export function Gate({ cfg, answers, save, readOnly, secondLabel }) {
  const items = Array.isArray(cfg.items) ? cfg.items : []
  const ticks = obj(answers.ticks)
  function toggle(i) {
    if (readOnly) return
    const next = { ...ticks, [String(i)]: ticks[String(i)] !== true }
    const all = items.every((_, j) => next[String(j)] === true)
    quiet(save({ ticks: next }, { done: all }))
  }
  const allDone = items.length > 0 && items.every((_, j) => ticks[String(j)] === true)
  return (
    <div className="ps-ex">
      <div className="ps-checks">
        {items.map((t, i) => (
          <label className={ticks[String(i)] === true ? 'ps-check ps-check--on' : 'ps-check'} key={i}>
            <input type="checkbox" checked={ticks[String(i)] === true} onChange={() => toggle(i)} {...readOnlyControl(readOnly)} />
            <span className="ps-check-text">{t}{i === 2 && secondLabel ? <span className="ps-small ps-check-sub">{COPY.yourSecond(secondLabel)}</span> : null}</span>
          </label>
        ))}
      </div>
      {allDone && <p className="ps-fb ps-fb--right ps-gate-open" aria-live="polite"><span className="ps-fb-tag">{COPY.gateOpen}</span>{COPY.gateOpenLine}</p>}
      {cfg.note_md && <Md text={cfg.note_md} className="ps-gate-note" />}
      <p className="ps-gate-fin"><span className="ps-stroke">{COPY.postTheLetters}</span></p>
    </div>
  )
}
